import React, { forwardRef } from 'react';
import { numberToWords } from '../utils/formatCurrency';
import { LOGO_DATA_URL } from '../utils/logoData';

const fmt = (n) => '\u20B9' + parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};

const LabelValue = ({ label, value }) => (
  <div style={{ fontSize: '8.5px', color: '#555', lineHeight: 1.5 }}>
    <span style={{ fontWeight: 700, color: '#000' }}>{label}</span> {value}
  </div>
);

const thStyle = {
  background: '#f5f5f5', fontWeight: 700, fontSize: '7.5px',
  padding: '2.5mm 2.5mm', borderBottom: '2px solid #ddd', textTransform: 'uppercase', letterSpacing: '0.2px',
};
const tdStyle = { padding: '2.5mm 2.5mm', borderBottom: '1px solid #eee', fontSize: '8.5px' };

const InvoicePreview = forwardRef(({ form, subtotal, discount = 0, totalCgst, totalSgst, totalIgst, grandTotal, itemSubtotal, itemGst, itemTotal, internal = false }, ref) => {
  const cin = (form.companyCIN || '').replace(/^CIN:\s*/i, '');
  const gstin = (form.companyGSTIN || '').replace(/^GSTIN:\s*/i, '');
  const isIntra = form.supplyType === 'intra';
  const hasSchedule = form.paymentSchedule && form.paymentSchedule.length > 0;

  return (
    <div ref={ref} style={{
      width: '210mm', height: '297mm', margin: '0 auto', padding: '12mm 14mm',
      fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: '9px',
      color: '#000', background: '#fff', boxSizing: 'border-box', lineHeight: 1.45,
      display: 'flex', flexDirection: 'column',
    }}>

      {/* ── Logo + TAX INVOICE ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5mm' }}>
        <img src={LOGO_DATA_URL} alt="Logo" style={{ width: '48mm', height: 'auto' }} />
        <div style={{ fontSize: '24px', fontWeight: 700, color: '#333', letterSpacing: '1px' }}>TAX INVOICE</div>
      </div>

      {/* ── Company (left) + Invoice meta (right) ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4mm' }}>
        <div style={{ maxWidth: '58%' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '1.5mm' }}>{form.companyName}</div>
          {form.companyAddress && (
            <div style={{ fontSize: '8.5px', color: '#555', lineHeight: 1.5 }}>
              <span style={{ fontWeight: 700, color: '#000' }}>Head Office:</span><br />
              {form.companyAddress.split('\n').map((l, i) => <span key={i}>{l}<br /></span>)}
            </div>
          )}
          {form.companyPhone && <LabelValue label="Ph:" value={form.companyPhone} />}
          {form.companyEmail && <LabelValue label="Email:" value={form.companyEmail} />}
          {form.companyWebsite && <LabelValue label="Website:" value={form.companyWebsite} />}
          {cin && <LabelValue label="CIN:" value={cin} />}
          {gstin && <LabelValue label="GSTIN:" value={gstin} />}
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '11px', color: '#666' }}># {form.invoiceNumber}</div>
          <div style={{ marginTop: '2mm' }}>
            <div style={{ fontSize: '8px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {hasSchedule ? 'Total Amount' : 'Balance Due'}
            </div>
            <div style={{ fontSize: '20px', fontWeight: 700, marginTop: '1mm' }}>{fmt(grandTotal)}</div>
          </div>
        </div>
      </div>

      <div style={{ borderTop: '1px solid #ddd', margin: '3mm 0' }} />

      {/* ── Bill To + Dates ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4mm' }}>
        <div style={{ maxWidth: '50%' }}>
          <div style={{ fontSize: '7.5px', color: '#888', textTransform: 'uppercase', marginBottom: '1mm' }}>Bill To</div>
          {form.billTo && form.billTo.split('\n').map((l, i) => (
            <div key={i} style={i === 0 ? { fontSize: '12px', fontWeight: 700 } : { fontSize: '9px', color: '#333' }}>{l}</div>
          ))}
          {form.billToGSTIN && (
            <div style={{ fontSize: '8.5px', marginTop: '1mm' }}><span style={{ fontWeight: 700 }}>GSTIN:</span> {form.billToGSTIN}</div>
          )}
        </div>
        <table style={{ fontSize: '8.5px', borderCollapse: 'collapse' }}>
          <tbody>
            {[
              ['Invoice Date :', fmtDate(form.invoiceDate)],
              ['Terms :', form.terms],
              ...(!hasSchedule ? [['Due Date :', fmtDate(form.dueDate)]] : []),
              ['Place of Supply :', form.placeOfSupply],
              ['Reverse Charge :', form.reverseCharge ? 'Yes' : 'No'],
            ].map(([lbl, val], i) => (
              <tr key={i}>
                <td style={{ padding: '1mm 5mm 1mm 0', color: '#888' }}>{lbl}</td>
                <td style={{ padding: '1mm 0', textAlign: 'right', fontWeight: 500 }}>{val}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Items Table ── */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2mm', fontSize: '8.5px' }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: '6mm', textAlign: 'center' }}>#</th>
            <th style={{ ...thStyle, textAlign: 'left' }}>Description</th>
            <th style={{ ...thStyle, width: '13mm', textAlign: 'right' }}>Qty</th>
            <th style={{ ...thStyle, width: '20mm', textAlign: 'right' }}>Rate</th>
            <th style={{ ...thStyle, width: '22mm', textAlign: 'right' }}>Taxable</th>
            <th style={{ ...thStyle, width: '12mm', textAlign: 'right' }}>GST%</th>
            {isIntra ? (
              <>
                <th style={{ ...thStyle, width: '20mm', textAlign: 'right' }}>CGST</th>
                <th style={{ ...thStyle, width: '20mm', textAlign: 'right' }}>SGST</th>
              </>
            ) : (
              <th style={{ ...thStyle, width: '20mm', textAlign: 'right' }}>IGST</th>
            )}
            <th style={{ ...thStyle, width: '22mm', textAlign: 'right' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((item, i) => {
            const gstPct = parseFloat(item.gst) || 0;
            const taxable = itemSubtotal(item);
            const gstAmt = itemGst(item);
            const total = itemTotal(item);
            return (
              <tr key={i} style={i % 2 === 0 ? { background: '#fafafa' } : {}}>
                <td style={{ ...tdStyle, textAlign: 'center', color: '#888' }}>{i + 1}</td>
                <td style={tdStyle}>{item.description || ''}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{parseFloat(item.qty || 0).toFixed(2)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(item.rate)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(taxable)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{gstPct > 0 ? `${gstPct}%` : '-'}</td>
                {isIntra ? (
                  <>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{gstAmt > 0 ? fmt(gstAmt / 2) : '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{gstAmt > 0 ? fmt(gstAmt / 2) : '-'}</td>
                  </>
                ) : (
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{gstAmt > 0 ? fmt(gstAmt) : '-'}</td>
                )}
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{fmt(total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ── Totals (right-aligned) ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '3mm' }}>
        <table style={{ fontSize: '9px', borderCollapse: 'collapse', minWidth: '60mm' }}>
          <tbody>
            <tr>
              <td style={{ padding: '1.5mm 6mm 1.5mm 0', color: '#555' }}>Taxable Value</td>
              <td style={{ padding: '1.5mm 0', textAlign: 'right' }}>{fmt(subtotal)}</td>
            </tr>
            {discount > 0 && (
              <tr>
                <td style={{ padding: '1.5mm 6mm 1.5mm 0', color: '#555' }}>Discount</td>
                <td style={{ padding: '1.5mm 0', textAlign: 'right' }}>-{fmt(discount)}</td>
              </tr>
            )}
            {(() => {
              const gstRates = {};
              form.items.forEach(item => {
                const rate = parseFloat(item.gst) || 0;
                if (rate > 0) { gstRates[rate] = (gstRates[rate] || 0) + itemGst(item); }
              });
              return Object.entries(gstRates).map(([rate, amt]) => {
                const r = parseFloat(rate);
                return isIntra ? (
                  <React.Fragment key={rate}>
                    <tr><td style={{ padding: '1.5mm 6mm 1.5mm 0', color: '#555' }}>CGST @ {r / 2}%</td><td style={{ padding: '1.5mm 0', textAlign: 'right' }}>{fmt(amt / 2)}</td></tr>
                    <tr><td style={{ padding: '1.5mm 6mm 1.5mm 0', color: '#555' }}>SGST @ {r / 2}%</td><td style={{ padding: '1.5mm 0', textAlign: 'right' }}>{fmt(amt / 2)}</td></tr>
                  </React.Fragment>
                ) : (
                  <tr key={rate}><td style={{ padding: '1.5mm 6mm 1.5mm 0', color: '#555' }}>IGST @ {r}%</td><td style={{ padding: '1.5mm 0', textAlign: 'right' }}>{fmt(amt)}</td></tr>
                );
              });
            })()}
            <tr>
              <td style={{ padding: '2mm 6mm 2mm 0', fontWeight: 700, borderTop: '2px solid #000', fontSize: '10px' }}>Total</td>
              <td style={{ padding: '2mm 0', textAlign: 'right', fontWeight: 700, borderTop: '2px solid #000', fontSize: '10px' }}>{fmt(grandTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Total in words */}
      <div style={{ textAlign: 'right', marginBottom: '4mm' }}>
        <span style={{ fontSize: '8px', color: '#888' }}>Total In Words: </span>
        <span style={{ fontSize: '8.5px', fontWeight: 700 }}>{numberToWords(grandTotal)}</span>
      </div>

      {/* ── Payment Schedule ── */}
      {hasSchedule && (
        <div style={{ borderTop: '1px solid #ddd', paddingTop: '3mm', marginBottom: '4mm' }}>
          <div style={{ fontSize: '9px', fontWeight: 700, marginBottom: '2mm' }}>Payment Schedule</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.5px' }}>
            <thead>
              <tr>
                <th style={{ padding: '1.5mm 2.5mm', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: 700, fontSize: '7.5px', color: '#555' }}>Installment</th>
                <th style={{ padding: '1.5mm 2.5mm', textAlign: 'right', borderBottom: '1px solid #ddd', fontWeight: 700, fontSize: '7.5px', color: '#555' }}>%</th>
                <th style={{ padding: '1.5mm 2.5mm', textAlign: 'right', borderBottom: '1px solid #ddd', fontWeight: 700, fontSize: '7.5px', color: '#555' }}>Amount</th>
                <th style={{ padding: '1.5mm 2.5mm', textAlign: 'right', borderBottom: '1px solid #ddd', fontWeight: 700, fontSize: '7.5px', color: '#555' }}>Due Date</th>
              </tr>
            </thead>
            <tbody>
              {form.paymentSchedule.map((ps, i) => {
                const pct = parseFloat(ps.percent) || 0;
                return (
                  <tr key={i} style={i % 2 === 0 ? { background: '#fafafa' } : {}}>
                    <td style={{ padding: '1.5mm 2.5mm', borderBottom: '1px solid #eee' }}>{ps.label || `Installment ${i + 1}`}</td>
                    <td style={{ padding: '1.5mm 2.5mm', borderBottom: '1px solid #eee', textAlign: 'right' }}>{pct}%</td>
                    <td style={{ padding: '1.5mm 2.5mm', borderBottom: '1px solid #eee', textAlign: 'right', fontWeight: 600 }}>{fmt(grandTotal * pct / 100)}</td>
                    <td style={{ padding: '1.5mm 2.5mm', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                      {fmtDate(ps.dueDate)}
                      {internal && ps.originalDueDate && ps.originalDueDate !== ps.dueDate && (
                        <div style={{ fontSize: '7px', color: '#B45309' }}>
                          was {fmtDate(ps.originalDueDate)}
                          {ps.dueDateHistory?.length > 1 && ` · postponed ${ps.dueDateHistory.length}×`}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Account Details ── */}
      {(form.accountName || form.accountNumber || form.bankName || form.ifsc) && (
        <div style={{ borderTop: '1px solid #ddd', paddingTop: '3mm', marginBottom: '4mm' }}>
          <div style={{ fontSize: '9px', fontWeight: 700, marginBottom: '2mm' }}>Account Details</div>
          <table style={{ fontSize: '8.5px', borderCollapse: 'collapse' }}>
            <tbody>
              {[
                form.accountName && ['Account Name', form.accountName],
                form.accountNumber && ['Account No', form.accountNumber],
                form.bankName && ['Bank', form.bankName],
                form.ifsc && ['IFSC', form.ifsc],
              ].filter(Boolean).map(([lbl, val], i) => (
                <tr key={i}>
                  <td style={{ padding: '0.8mm 5mm 0.8mm 0', fontWeight: 700, color: '#000', whiteSpace: 'nowrap' }}>{lbl}:</td>
                  <td style={{ padding: '0.8mm 0', color: '#555' }}>{val}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Spacer to push footer to bottom ── */}
      <div style={{ flex: 1 }} />

      {/* ── Footer ── */}
      {form.signatureMode === 'physical' ? (
        <div>
          <div style={{ fontSize: '9px', fontWeight: 700 }}>Authorized Signature</div>
          <div style={{ width: '55mm', borderTop: '1px solid #000', marginTop: '12mm' }} />
        </div>
      ) : (
        <div style={{ borderTop: '1px solid #eee', paddingTop: '2mm', fontSize: '7.5px', color: '#aaa', textAlign: 'center' }}>
          This is a computer-generated invoice and is valid without a signature.
        </div>
      )}
    </div>
  );
});

InvoicePreview.displayName = 'InvoicePreview';
export default InvoicePreview;
